import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCalculator } from "react-icons/fa";
import {
  useGenerateMultiProductPDF,
  downloadBlob,
} from "../utils/pdfGenerator";
import { cleanCurrency } from "../Components/OfferGenerator/calculationUtils";
import {
  STEPS,
  okkaliUnitPrices,
  defaultMachinePrices,
  machineModelOptions,
} from "../Components/OfferGenerator/constants";
import {
  getDefaultGeneralValues,
  getDefaultMachineInputs,
  calculateProductCosts,
  calculateProductUnitPrice,
} from "../Components/OfferGenerator/calculationUtils";
import ConfigStep from "../Components/OfferGenerator/ConfigStep";
import CalculationStep from "../Components/OfferGenerator/CalculationStep";
import PdfStep from "../Components/OfferGenerator/PdfStep";
import WarningModal from "../Components/OfferGenerator/WarningModal";
import useAuth from "../Hooks/useAuth";
import OfferFlowHero from "../Components/OfferGenerator/OfferFlowHero";
import { PageShell, PageHeader } from "../Components/shared";

// Bir ürünün hesaplama state'inin başlangıç değerleri.
// "Diğer" makine tutarı adet × birim fiyattan hesaplanır; böylece kullanıcı
// makine alanlarına hiç dokunmasa bile amortisman/servis maliyeti sıfır kalmaz.
const createInitialProductState = (product, okkaliBirimFiyat) => {
  const digerFiyat = defaultMachinePrices[product] || "0";
  const makineAdetDiger = "1";
  const makineTutariDiger = (
    (parseFloat(digerFiyat) || 0) * (parseFloat(makineAdetDiger) || 0)
  ).toFixed(0);

  return {
    tuketim: "",
    makineAdetOkkali: "1",
    makineAdetDiger,
    digerFiyat,
    gunlukTuketim: "",
    aylikTuketim: "",
    yillikTuketim: "",
    makineTutariDiger,
    birimMakineMaliyetiDiger: "0",
    cayKahveGrDiger: "0",
    sekerGrDiger: "0",
    kagitBardakKatsayiDiger: "0",
    elemanIhtiyaciDiger: "0",
    elemanVerimliligiDiger: "0",
    kullaniciPersonelVerimliligiDiger: "0",
    kayipKacakOraniDiger: "0",
    servisBakimMaliyetiDiger: "0",
    okkaliBirimFiyat,
    digerBirimFiyat: "0",
  };
};

export default function OfferGeneratorPage() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { createMultiProductPDF } = useGenerateMultiProductPDF();
  const [currentStep, setCurrentStep] = useState(STEPS.CONFIG);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [brandName, setBrandName] = useState("");
  const [loading, setLoading] = useState(false);
  // { blob, fileName, url } — önizleme ile indirmenin ortak kaynağı
  const [pdfPreview, setPdfPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Agreement verileri
  const [agreementData, setAgreementData] = useState({});

  // Calculation verileri
  const [productStates, setProductStates] = useState({});
  const [machineInputParams, setMachineInputParams] = useState({});
  const [generalParams, setGeneralParams] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const resultsContainerRef = useRef(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (!notification) return;
    const timeout = setTimeout(() => setNotification(null), 6000);
    return () => clearTimeout(timeout);
  }, [notification]);

  // Sayfadan çıkarken açık kalan blob URL'ini serbest bırak (yerine yenisi
  // konulduğunda eskisi zaten setter içinde revoke ediliyor).
  const pdfPreviewRef = useRef(null);
  useEffect(() => {
    pdfPreviewRef.current = pdfPreview;
  }, [pdfPreview]);
  useEffect(
    () => () => {
      if (pdfPreviewRef.current?.url) {
        window.URL.revokeObjectURL(pdfPreviewRef.current.url);
      }
    },
    []
  );

  const showNotification = (type, message) => {
    setNotification({ type, message, id: Date.now() });
  };

  // Veri değiştiğinde eski önizleme geçersizdir
  const clearPdfPreview = () => {
    setPdfPreview((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const getNotificationTheme = (type) => {
    switch (type) {
      case "success":
        return {
          className: "bg-emerald-50 border-emerald-200 text-emerald-800",
        };
      case "error":
        return { className: "bg-red-50 border-red-200 text-red-800" };
      default:
        return { className: "bg-blue-50 border-blue-200 text-blue-800" };
    }
  };

  // Step 1: Ürün seçimi
  const toggleProduct = (productType) => {
    setSelectedProducts((prev) =>
      prev.includes(productType)
        ? prev.filter((p) => p !== productType)
        : [...prev, productType]
    );
  };

  // Step 2: Agreement
  const updateAgreementData = (product, field, value) => {
    const defaultModel = machineModelOptions[product]?.[0] || "";
    setAgreementData((prev) => ({
      ...prev,
      [product]: {
        ...prev[product],
        makineModeli: prev[product]?.makineModeli || defaultModel,
        birimAdetFiyat:
          prev[product]?.birimAdetFiyat || okkaliUnitPrices[product] || "",
        [field]: value,
      },
    }));
  };

  const handleConfigContinue = () => {
    if (selectedProducts.length === 0) {
      showNotification("error", "Lütfen en az bir ürün seçiniz.");
      return;
    }

    // Calculation için state hazırla. Daha önce doldurulmuş ürünlerin verisi
    // korunur (adım 1'e geri dönüp tekrar devam etmek girdileri silmemeli),
    // sadece yeni seçilen ürünler için varsayılan üretilir.
    const nextStates = {};
    const nextMachineInputs = {};
    const nextGeneralParams = {};
    const nextAgreementData = {};

    selectedProducts.forEach((product) => {
      const defaultModel = machineModelOptions[product]?.[0] || "";
      const agreementBirimFiyat =
        agreementData[product]?.birimAdetFiyat || okkaliUnitPrices[product];

      // PDF'e giden anlaşma bilgisi: kullanıcı dokunmasa bile ekranda görünen
      // varsayılanlarla (ilk model + liste fiyatı) doldurulur.
      nextAgreementData[product] = {
        ...agreementData[product],
        makineModeli: agreementData[product]?.makineModeli || defaultModel,
        birimAdetFiyat: agreementBirimFiyat,
      };

      nextStates[product] =
        productStates[product] ||
        createInitialProductState(product, agreementBirimFiyat);
      nextMachineInputs[product] =
        machineInputParams[product] || getDefaultMachineInputs(product);
      nextGeneralParams[product] =
        generalParams[product] || getDefaultGeneralValues(product);
    });

    setAgreementData(nextAgreementData);
    setProductStates(nextStates);
    setMachineInputParams(nextMachineInputs);
    setGeneralParams(nextGeneralParams);
    setCurrentStep(STEPS.CALCULATION);
  };

  // Step 3: Calculation - Hesaplama fonksiyonları
  const updateProductInput = (product, key, value) => {
    let cleanedValue;

    if (key === "makineAdetOkkali" || key === "makineAdetDiger") {
      cleanedValue = value.replace(/^0+/, "") || "";
    } else if (
      key.includes("Fiyat") ||
      key.includes("Maliyet") ||
      key.includes("Tutari")
    ) {
      cleanedValue = cleanCurrency(value);
    } else {
      cleanedValue = value;
    }

    setProductStates((prev) => {
      const updated = { ...prev };
      const productState = { ...prev[product], [key]: cleanedValue };

      if (key === "tuketim") {
        const aylikValue = parseFloat(cleanedValue) || 0;
        if (aylikValue > 0) {
          const gunlukTuketim = aylikValue / 30;
          const yillikTuketim = aylikValue * 12;
          productState.gunlukTuketim = gunlukTuketim.toFixed(0);
          productState.aylikTuketim = aylikValue.toFixed(0);
          productState.yillikTuketim = yillikTuketim.toFixed(0);

          calculateProductCosts(
            product,
            productState,
            generalParams[product] || getDefaultGeneralValues(product),
            machineInputParams[product] || getDefaultMachineInputs(product),
            { ...prev, [product]: productState },
            undefined
          );
        } else {
          productState.gunlukTuketim = "";
          productState.aylikTuketim = "";
          productState.yillikTuketim = "";
          productState.cayKahveGrDiger = "0";
          productState.sekerGrDiger = "0";
          productState.kagitBardakKatsayiDiger = "0";
          productState.elemanIhtiyaciDiger = "0";
          productState.elemanVerimliligiDiger = "0";
          productState.kullaniciPersonelVerimliligiDiger = "0";
          productState.kayipKacakOraniDiger = "0";
          productState.servisBakimMaliyetiDiger = "0";
          productState.digerBirimFiyat = "0";
        }
      }

      if (key === "makineAdetDiger" || key === "digerFiyat") {
        const aylikTuketim = parseFloat(productState.aylikTuketim || "0") || 0;

        if (aylikTuketim > 0) {
          // Tüketim girilmişse tüm maliyet kalemleri tek yerden hesaplanır
          // (makine tutarı da calculateProductCosts içinde türetilir).
          calculateProductCosts(
            product,
            productState,
            generalParams[product] || getDefaultGeneralValues(product),
            machineInputParams[product] || getDefaultMachineInputs(product),
            prev
          );
        } else {
          const makineFiyati =
            parseFloat(
              cleanCurrency(
                productState.digerFiyat || defaultMachinePrices[product] || "0"
              )
            ) || 0;
          const makineAdet =
            parseFloat(productState.makineAdetDiger || "0") || 0;
          productState.makineTutariDiger = (makineFiyati * makineAdet).toFixed(
            0
          );
          productState.birimMakineMaliyetiDiger = "0";
          productState.servisBakimMaliyetiDiger = "0";
          calculateProductUnitPrice(product, productState);
        }
      }

      updated[product] = productState;
      return updated;
    });
  };

  // Parametre değişikliği sonrası ürünün maliyetlerini yeniden hesaplar.
  // Updater saf tutulur (mevcut state objesi mutasyona uğramaz, updater
  // içinden başka bir setState çağrılmaz) — React 18 batching'inde güvenli.
  const recalculateProduct = (product, machineInputs, productGeneralParams) => {
    setProductStates((prev) => {
      const current = prev[product];
      if (!current || !(parseFloat(current.aylikTuketim || "0") > 0)) {
        return prev;
      }
      const next = { ...current };
      calculateProductCosts(
        product,
        next,
        productGeneralParams,
        machineInputs,
        prev
      );
      return { ...prev, [product]: next };
    });
  };

  // patch: { key: value, ... } — drawer tüm alanları tek çağrıda gönderir.
  const updateMachineInputParams = (product, patch) => {
    const nextMachineInputs = {
      ...(machineInputParams[product] || getDefaultMachineInputs(product)),
      ...patch,
    };
    setMachineInputParams((prev) => ({ ...prev, [product]: nextMachineInputs }));
    recalculateProduct(
      product,
      nextMachineInputs,
      generalParams[product] || getDefaultGeneralValues(product)
    );
  };

  const updateGeneralParams = (product, patch) => {
    const nextGeneralParams = {
      ...(generalParams[product] || getDefaultGeneralValues(product)),
      ...patch,
    };
    setGeneralParams((prev) => ({ ...prev, [product]: nextGeneralParams }));
    recalculateProduct(
      product,
      machineInputParams[product] || getDefaultMachineInputs(product),
      nextGeneralParams
    );
  };

  const handleCalculate = () => {
    const hasEmptyConsumption = selectedProducts.some((product) => {
      const state = productStates[product];
      return !state?.tuketim || parseFloat(state.tuketim) <= 0;
    });

    const hasEmptyMachineCount = selectedProducts.some((product) => {
      const state = productStates[product];
      return (
        !state?.makineAdetOkkali || parseFloat(state.makineAdetOkkali) <= 0
      );
    });

    if (hasEmptyConsumption || hasEmptyMachineCount) {
      setShowWarningModal(true);
      return;
    }

    setProductStates((prev) => {
      const updated = { ...prev };
      selectedProducts.forEach((product) => {
        const current = prev[product];
        if (current && parseFloat(current.aylikTuketim || "0") > 0) {
          const next = { ...current };
          calculateProductCosts(
            product,
            next,
            generalParams[product] || getDefaultGeneralValues(product),
            machineInputParams[product] || getDefaultMachineInputs(product),
            prev
          );
          updated[product] = next;
        }
      });
      return updated;
    });

    // Yeni hesaplama → eldeki önizleme bayat
    clearPdfPreview();
    setShowResults(true);
    setCurrentStep(STEPS.PDF);
    setTimeout(() => {
      if (resultsContainerRef.current) {
        resultsContainerRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 150);
  };

  const handleReset = () => {
    const resetStates = {};
    const resetMachineInputs = {};
    const resetGeneralParams = {};

    selectedProducts.forEach((product) => {
      const agreementBirimFiyat =
        agreementData[product]?.birimAdetFiyat || okkaliUnitPrices[product];

      resetStates[product] = createInitialProductState(
        product,
        agreementBirimFiyat
      );
      resetMachineInputs[product] = getDefaultMachineInputs(product);
      resetGeneralParams[product] = getDefaultGeneralValues(product);
    });

    setProductStates(resetStates);
    setMachineInputParams(resetMachineInputs);
    setGeneralParams(resetGeneralParams);
    setBrandName("");
    setShowResults(false);
    clearPdfPreview();
  };

  // Sunucuya gidecek teklif verisi — önizleme ve indirme aynı gövdeyi kullanır.
  const buildPdfData = () => ({
    brandName: brandName || undefined,
    products: selectedProducts.map((product) => {
      const state = productStates[product] || {};
      const cleanOkkaliBirimFiyat = state.okkaliBirimFiyat
        ? cleanCurrency(state.okkaliBirimFiyat.toString())
        : okkaliUnitPrices[product];
      const cleanDigerBirimFiyat = state.digerBirimFiyat
        ? cleanCurrency(state.digerBirimFiyat.toString())
        : "0";

      const agreementMakineModeli =
        agreementData[product]?.makineModeli ||
        machineModelOptions[product]?.[0] ||
        "";
      const agreementBirimAdetFiyat =
        agreementData[product]?.birimAdetFiyat || cleanOkkaliBirimFiyat;

      return {
        productType: product,
        productState: {
          ...state,
          okkaliBirimFiyat: cleanOkkaliBirimFiyat,
          digerBirimFiyat: cleanDigerBirimFiyat,
        },
        machineInputs:
          machineInputParams[product] || getDefaultMachineInputs(product),
        generalParams:
          generalParams[product] || getDefaultGeneralValues(product),
        agreementData: {
          makineModeli: agreementMakineModeli
            ? String(agreementMakineModeli).trim()
            : "",
          birimAdetFiyat: agreementBirimAdetFiyat || cleanOkkaliBirimFiyat,
        },
      };
    }),
  });

  // RTK Query / fetch hatalarını okunur mesaja çevirir.
  const describePdfError = (error) => {
    let errorMessage = "Bilinmeyen bir hata oluştu";

    if (error?.data) {
      errorMessage =
        typeof error.data === "string"
          ? error.data
          : error.data?.message ||
            error.data?.error ||
            JSON.stringify(error.data);
    } else if (error?.error) {
      errorMessage = error.error;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    }

    if (error?.status === "FETCH_ERROR") {
      errorMessage = `Bağlantı hatası: ${errorMessage}`;
    } else if (typeof error?.status === "number") {
      errorMessage = `[${error.status}] ${errorMessage}`;
    }

    return errorMessage;
  };

  const canGeneratePdf = () => {
    if (selectedProducts.length === 0) {
      showNotification(
        "error",
        "PDF oluşturmak için en az bir ürün seçip hesaplamayı tamamlayın."
      );
      return false;
    }
    if (!auth?.accessToken) {
      showNotification("error", "Oturum açmanız gerekiyor. Lütfen giriş yapın.");
      return false;
    }
    return true;
  };

  // Önizleme: PDF sunucuda üretilir, blob URL olarak iframe'de gösterilir.
  // İndirme aynı blob'u kullandığı için ekrandaki belge ile dosya birebir aynı.
  const handleCreatePreview = async () => {
    if (!canGeneratePdf()) return;

    setPreviewLoading(true);
    try {
      const { blob, fileName } = await createMultiProductPDF(
        buildPdfData(),
        auth.accessToken
      );
      setPdfPreview((prev) => {
        if (prev?.url) window.URL.revokeObjectURL(prev.url);
        return { blob, fileName, url: window.URL.createObjectURL(blob) };
      });
    } catch (error) {
      showNotification(
        "error",
        `PDF önizlemesi oluşturulamadı: ${describePdfError(error)}`
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    // Önizleme hazırsa aynı dosya indirilir; değilse önce üretilir.
    if (pdfPreview?.blob) {
      downloadBlob(pdfPreview.blob, pdfPreview.fileName);
      showNotification("success", "PDF indirildi.");
      return;
    }

    if (!canGeneratePdf()) return;

    showNotification("info", "PDF hazırlanıyor, indirme otomatik başlayacak.");
    setLoading(true);
    try {
      const { blob, fileName } = await createMultiProductPDF(
        buildPdfData(),
        auth.accessToken
      );
      downloadBlob(blob, fileName);
      setPdfPreview((prev) => {
        if (prev?.url) window.URL.revokeObjectURL(prev.url);
        return { blob, fileName, url: window.URL.createObjectURL(blob) };
      });
      showNotification("success", "PDF hazır! İndirme başlatıldı.");
    } catch (error) {
      showNotification(
        "error",
        `PDF oluşturulurken bir hata oluştu: ${describePdfError(error)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const notificationTheme = notification
    ? getNotificationTheme(notification.type)
    : null;

  return (
    <PageShell>
      {/* Sabit üst bar — boşluk dış margin'den değil içerik padding'inden gelir */}
      <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
        <PageHeader
          icon={FaCalculator}
          iconColor="blue"
          title="Detaylı Teklif"
          subtitle="Kapsamlı hesaplama ve analiz ile teklif oluşturun"
          actions={[
            {
              label: "Teklif Tipi Seçimi",
              icon: FaArrowLeft,
              onClick: () => navigate("/Anasayfa/Teklif"),
            },
          ]}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-5">
        <OfferFlowHero
          selectedCount={selectedProducts.length}
          brandName={brandName}
          currentStep={currentStep}
          onBrandNameChange={setBrandName}
        />

        {notification && (
          <div
            role="status"
            className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-sm font-semibold ${notificationTheme.className}`}
          >
            <span>{notification.message}</span>
            <button
              type="button"
              onClick={() => setNotification(null)}
              className="shrink-0 text-lg leading-none opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Bildirimi kapat"
            >
              ×
            </button>
          </div>
        )}

        {/* Step Content */}
        {currentStep === STEPS.CONFIG && (
          <ConfigStep
            selectedProducts={selectedProducts}
            brandName={brandName}
            onToggleProduct={toggleProduct}
            onContinue={handleConfigContinue}
          />
        )}
        {currentStep === STEPS.CALCULATION && (
          <CalculationStep
            selectedProducts={selectedProducts}
            productStates={productStates}
            agreementData={agreementData}
            machineInputParams={machineInputParams}
            generalParams={generalParams}
            onUpdateProductInput={updateProductInput}
            onUpdateAgreementData={updateAgreementData}
            onUpdateMachineInputParams={updateMachineInputParams}
            onUpdateGeneralParams={updateGeneralParams}
            onCalculate={handleCalculate}
            onReset={handleReset}
            onBack={() => setCurrentStep(STEPS.CONFIG)}
          />
        )}
        {currentStep === STEPS.PDF && (
          <PdfStep
            selectedProducts={selectedProducts}
            productStates={productStates}
            showResults={showResults}
            resultsContainerRef={resultsContainerRef}
            onGeneratePDF={handleGeneratePDF}
            loading={loading}
            previewUrl={pdfPreview?.url || null}
            previewFileName={pdfPreview?.fileName || ""}
            previewLoading={previewLoading}
            onCreatePreview={handleCreatePreview}
            onBack={() => setCurrentStep(STEPS.CALCULATION)}
          />
        )}
      </div>

      {/* Warning Modal */}
      {showWarningModal && (
        <WarningModal onClose={() => setShowWarningModal(false)} />
      )}
    </PageShell>
  );
}
